
import React, { useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Upload, Link, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { Creative } from "../../../types";
import CreativePreview from "./CreativePreview";

interface StandardCreativeUploadProps {
  mediaOwner: string;
  creatives: Creative[];
  onMediaOwnerFileChange: (mediaOwnerName: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMediaOwnerCreative: (mediaOwnerName: string, index: number) => void;
  onUpdateMediaOwnerCreativeDays?: (mediaOwnerName: string, creativeIndex: number, days: number[]) => void;
  isUploading: boolean;
  onUrlSubmit?: (mediaOwnerName: string, url: string) => void;
}

const StandardCreativeUpload = ({
  mediaOwner,
  creatives,
  onMediaOwnerFileChange,
  onRemoveMediaOwnerCreative,
  onUpdateMediaOwnerCreativeDays,
  isUploading,
  onUrlSubmit
}: StandardCreativeUploadProps) => {
  const { t, language } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlError, setUrlError] = useState("");

  const handleAreaClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const syntheticEvent = {
        target: { files },
      } as React.ChangeEvent<HTMLInputElement>;

      onMediaOwnerFileChange(mediaOwner, syntheticEvent);
    }
  };

  const validateUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        setUrlError(language === 'ar' ? 'يجب أن يبدأ الرابط بـ http:// أو https://' : 'URL must start with http:// or https://');
        return false;
      }
      setUrlError("");
      return true;
    } catch {
      setUrlError(language === 'ar' ? 'الرابط غير صالح' : 'Invalid URL format');
      return false;
    }
  };

  const handleUrlSubmit = () => {
    const trimmedUrl = urlValue.trim();
    if (!trimmedUrl) {
      setUrlError(language === 'ar' ? 'الرجاء إدخال رابط' : 'Please enter a URL');
      return;
    }
    
    if (!validateUrl(trimmedUrl)) {
      return;
    }

    if (onUrlSubmit) {
      onUrlSubmit(mediaOwner, trimmedUrl);
      setUrlValue("");
      setShowUrlInput(false);
    }
  };

  // Calculate taken days for each creative (days used by OTHER creatives)
  const getTakenDaysForCreative = (creativeIndex: number): number[] => {
    const taken: number[] = [];
    creatives.forEach((c, idx) => {
      if (idx !== creativeIndex && c.scheduledDays) {
        taken.push(...c.scheduledDays);
      }
    });
    return [...new Set(taken)];
  };

  // Only show scheduling UI when there are 2+ creatives
  const showSchedulingUI = creatives.length >= 2;

  return (
    <div className="space-y-4">
      {/* Show existing creatives */}
      {creatives.length > 0 && (
        <div className="space-y-3">
          {creatives.map((creative, index) => (
            <CreativePreview
              key={index}
              creative={creative}
              creativeIndex={index}
              onRemove={() => onRemoveMediaOwnerCreative(mediaOwner, index)}
              onUpdateScheduledDays={
                onUpdateMediaOwnerCreativeDays
                  ? (days) => onUpdateMediaOwnerCreativeDays(mediaOwner, index, days)
                  : undefined
              }
              takenDays={getTakenDaysForCreative(index)}
              showScheduling={showSchedulingUI}
              isUploading={isUploading}
            />
          ))}
        </div>
      )}

      {/* URL Input Mode */}
      {showUrlInput ? (
        <div className="border-2 border-dashed rounded-lg p-6 border-primary/30 bg-primary/5">
          <div className="flex flex-col items-center space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowUrlInput(false);
                setUrlValue("");
                setUrlError("");
              }}
              className="self-start -mt-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Link className="h-6 w-6 text-primary" />
            </div>
            
            <div className="w-full max-w-md space-y-3">
              <p className="text-center text-sm font-medium text-foreground">
                {language === 'ar' ? 'أدخل رابط الملفات' : 'Enter a link to your files'}
              </p>
              <p className="text-xs text-muted-foreground text-center">
                {language === 'ar' 
                  ? 'يمكنك إضافة روابط من Google Drive أو Dropbox أو أي رابط آخر' 
                  : 'You can add links from Google Drive, Dropbox, or any other source'}
              </p>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={urlValue}
                  onChange={(e) => {
                    setUrlValue(e.target.value);
                    setUrlError("");
                  }}
                  placeholder="https://drive.google.com/..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleUrlSubmit();
                    }
                  }}
                />
                <Button onClick={handleUrlSubmit} disabled={!urlValue.trim()}>
                  {language === 'ar' ? 'إضافة' : 'Add'}
                </Button>
              </div>
              {urlError && (
                <p className="text-xs text-destructive text-center">{urlError}</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Upload area - always visible for adding more files */
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-primary/50 hover:bg-muted/50'
          }`}
          onClick={handleAreaClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/jpeg,image/png,image/gif,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
            multiple={true}
            onChange={(e) => onMediaOwnerFileChange(mediaOwner, e)}
          />
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {creatives.length > 0 
                ? (language === 'ar' ? '+ إضافة المزيد من الملفات' : '+ Add more files')
                : (<>{t('dropImageOrVideo')} <span className="text-primary">{t('browse')}</span></>)
              }
            </p>
            <p className="text-xs text-primary font-medium">
              📁 {language === 'ar' ? 'يمكنك تحميل ملفات متعددة' : 'Multiple files supported'}
            </p>
            
            {/* URL Option Button */}
            {onUrlSubmit && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUrlInput(true);
                }}
                className="mt-2"
              >
                <Link className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'أو أضف رابط' : 'Or add a link'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardCreativeUpload;
